import React from 'react'
import { Flex, Segmented, Select } from 'antd'
import { BuiltInValueInput } from '@pushly/aqe'
import { valueShapeFor, type ValueShape } from '@pushly/aqe/lib/components/pquery-builder'
import type {
    ConditionSummaryContributor,
    ConditionValueValidator,
    PQueryBuilderField,
    ValueInputProps,
} from '@pushly/aqe'
import {
    buildEventPropertyReference,
    isEventPropertyReference,
    type EventPropertyReference,
} from './event-property-fields'
import { useEventProperties } from './event-property-context'
import { EVENT_BINDABLE_INPUT_KEY, type EventBindableField } from './event-bindable-fields'
import { SEGMENT_VALUE_INPUTS, SEGMENT_VALUE_VALIDATORS } from '../../../components/segment-query-builder/value-inputs'

type LiteralValue = unknown

/**
 * The value shapes one reference can stand in for.
 *
 * A pair of bounds, a version range and a named window are several values or a vocabulary rather
 * than one value, and an operator that takes no value has nothing to bind. The same field is
 * bindable under one operator and not another, so this is asked of the row's operator, not the field.
 */
const REFERENCE_SUBSTITUTABLE_SHAPES: ReadonlySet<ValueShape> = new Set(['scalar', 'list', 'version', 'geo', 'document-ids'])

const VALUE_SOURCE_LITERAL = 'literal'
const VALUE_SOURCE_EVENT = 'event'

/**
 * The two places a condition's value can come from.
 *
 * Named on the row rather than inferred, because the distinction is not cosmetic: a literal is
 * fixed when the journey is authored, while an event reference is resolved per event at routing
 * time and exists only for the lifetime of that one event.
 */
const VALUE_SOURCE_OPTIONS = [
    { label: 'Value', value: VALUE_SOURCE_LITERAL },
    { label: 'Event property', value: VALUE_SOURCE_EVENT },
]

/**
 * The value editor for a field that may hold a literal or a reference to the triggering event.
 *
 * Registered under {@link EVENT_BINDABLE_INPUT_KEY}, which the catalog names on every field offered
 * as event-bindable. That registration is the guard against an editor that does not recognise a
 * `{ "$event": … }` value: it hands the object to a text control, which shows it as `[object Object]`
 * and replaces it with a string on the first keystroke — irreversibly, and with the save not blocked.
 *
 * The literal half is the control the builder itself renders for the field, not a copy of it. A copy
 * agrees with the original only until the original gains something, such as searching a field's
 * values at their source, and then quietly stops offering it. A field that owned a control of its own
 * before it became bindable keeps that control instead.
 */
export const EventBindableValueInput: React.FC<ValueInputProps<LiteralValue>> = (props) => {
    const { value, onChange, disabled, size, getPopupContainer, operator, field } = props
    const eventProperties = useEventProperties()
    const boundReference = isEventPropertyReference(value) ? (value as EventPropertyReference) : undefined
    const source = boundReference ? VALUE_SOURCE_EVENT : VALUE_SOURCE_LITERAL

    const handleSourceChange = (next: string | number) => {
        if (next === VALUE_SOURCE_EVENT) {
            // An empty path rather than a guessed one: the reference is incomplete until a property
            // is chosen, and the registered completeness rule below says so.
            if (!boundReference) onChange(buildEventPropertyReference(''))
            return
        }

        // Dropping the reference clears the value outright. Carrying anything over would mean
        // inventing a literal the reader never wrote.
        if (boundReference) onChange(undefined)
    }

    // The literal control alone where one reference cannot fill the operator's slot. A reference
    // already stored stays editable, so a row the reader switched operator on is never stranded.
    if (!boundReference && !REFERENCE_SUBSTITUTABLE_SHAPES.has(valueShapeFor(operator, field))) {
        return <LiteralValueInput {...props} />
    }

    return (
        <Flex vertical gap={8} className="event-bindable-value-input">
            <Segmented
                size="small"
                disabled={disabled}
                value={source}
                options={VALUE_SOURCE_OPTIONS}
                onChange={handleSourceChange}
            />

            {source === VALUE_SOURCE_EVENT ? (
                <Select
                    size={size ?? 'small'}
                    disabled={disabled}
                    value={boundReference?.$event || undefined}
                    placeholder="Select an event property"
                    getPopupContainer={getPopupContainer}
                    onChange={(propertyPath: string) => onChange(buildEventPropertyReference(propertyPath))}
                    options={eventProperties.map((property) => ({
                        value: property.key,
                        label: property.label,
                    }))}
                    notFoundContent="This event type declares no properties"
                    showSearch
                    optionFilterProp="label"
                />
            ) : (
                <LiteralValueInput {...props} />
            )}
        </Flex>
    )
}

/**
 * The control a bindable field's literal half is authored through.
 *
 * Handed to the builder's own editor, so a literal is entered exactly as it is on a plain condition:
 * the same control for every value shape, the same search of a field's values at their source, and
 * whatever the builder adds next.
 */
const LiteralValueInput: React.FC<ValueInputProps<LiteralValue>> = (props) => {
    // A field that owned a control before it was made bindable authors its literal through that same
    // control. A polygon has no plain equivalent to fall back to, and a second renderer standing in for
    // one is how a drawn shape gets written back as an empty one.
    const delegatedInputKey = (props.field as EventBindableField).literalInputKey
    const DelegatedInput = delegatedInputKey ? SEGMENT_VALUE_INPUTS[delegatedInputKey] : undefined

    if (DelegatedInput) {
        // Handed the field as the catalog declared it. The control may resolve its own registration
        // from the key, and reading the one this editor is registered under would send it back here.
        const declaredField = { ...props.field, inputKey: delegatedInputKey }
        return <DelegatedInput {...props} field={declaredField} />
    }

    return <BuiltInValueInput {...props} />
}

/**
 * A reference naming no property yet.
 *
 * The builder's own rules judge a value against the shape the engine reads it as, and a reference
 * is an object that satisfies presence — so without this an unfinished binding would save as a
 * reference to nothing, which the router resolves to no value and no audience.
 */
const validateEventBinding: ConditionValueValidator = (args) => {
    const { value, field } = args

    // A literal is judged by whatever rule the field's own control registered, exactly as it was
    // before the field became bindable. Routing the field here replaced which validator the builder
    // resolves, so without this a delegated control's completeness rule would silently stop running
    // — and a rule that stops running reports nothing, which reads identically to a value that passed.
    if (!isEventPropertyReference(value)) {
        const delegatedInputKey = (field as EventBindableField).literalInputKey
        const delegated = delegatedInputKey ? SEGMENT_VALUE_VALIDATORS[delegatedInputKey] : undefined

        return delegated ? delegated(args) : null
    }

    return value.$event ? null : 'Select an event property'
}

/**
 * How a collapsed row reads a condition bound to an event property.
 *
 * Without this the row shows the stored object — `{…}`, or the JSON itself — which says neither
 * that the value is a reference nor which property it names. Both halves have to be on the row: a
 * reader scanning an audience needs to see that this one condition is answered per event, and needs
 * to see it in the same words the editor offered when they bound it.
 *
 * The property is named by whatever the registry called it, resolved through the same catalog the
 * editor's own picker is built from, so the two cannot come to call one property two things. A
 * property the registry no longer holds still reads as the path it names rather than disappearing —
 * the journey depends on that reference and hiding it would not make it any less bound.
 *
 * A literal declines, leaving the row on the builder's own reading: the field's literal half is the
 * control the builder would otherwise have rendered, so its value should read as it always has.
 */
export function createEventBindingSummary(
    eventProperties: PQueryBuilderField[],
    literalSummaries: Record<string, ConditionSummaryContributor> = {},
): ConditionSummaryContributor {
    return (context) => {
        const { value, field } = context

        if (!isEventPropertyReference(value)) {
            // A literal reads through the summary its own control registered, which is the one the
            // segment screen shows for it. Routing the field here replaced which summary the builder
            // resolves, so without this a prompt would read as its bare id again.
            const delegatedInputKey = (field as EventBindableField).literalInputKey
            return delegatedInputKey ? (literalSummaries[delegatedInputKey]?.(context) ?? null) : null
        }

        if (!value.$event) return [{ value: 'No event property selected' }]

        const named = eventProperties.find((property) => property.key === value.$event)
        return [{ label: 'event property', value: named?.label ?? value.$event }]
    }
}

/** The value editors and completeness rules an event-bindable catalog needs alongside its own. */
export const EVENT_BINDABLE_VALUE_INPUTS = {
    [EVENT_BINDABLE_INPUT_KEY]: EventBindableValueInput,
}

export const EVENT_BINDABLE_VALUE_VALIDATORS: Record<string, ConditionValueValidator> = {
    [EVENT_BINDABLE_INPUT_KEY]: validateEventBinding,
}
